import type { ApiResponse, Pagination } from "@/types";

import { BaseApi } from "../base.api";

/** Backend'dagi `AI_PROTOCOLS` (ADR 0012). */
export type AiProtocol = "ANTHROPIC" | "OPENAI_COMPATIBLE" | "FAKE";
export type AiStructuredMode = "TOOL" | "PROMPT";
export type AiKeyStatus = "SET" | "MISSING" | "UNREADABLE";

export const AI_FEATURES = [
  "QUIZ_GENERATION",
  "SUBMISSION_PREGRADE",
  "RISK_EXPLANATION",
  "COURSE_TUTOR",
  "QUIZ_REVIEW",
  "COURSE_PRECHECK",
  "CONTENT_SCREEN",
  "CATALOG_SEARCH",
  "REVIEW_SUMMARY",
  "COURSE_COPY",
] as const;
export type AiFeature = (typeof AI_FEATURES)[number];

export interface AiModel {
  id: number;
  name: string;
  protocol: AiProtocol;
  baseUrl: string | null;
  modelId: string;
  /** Kalitning oxirgi 4 belgisi — kalitning o'zi hech qachon kelmaydi. */
  apiKeyLast4: string | null;
  keyStatus: AiKeyStatus;
  /** 1M token narxi, mikro-dollar ($0.10 = 100 000). */
  inputPriceMicroUsd: number;
  outputPriceMicroUsd: number;
  isFree: boolean;
  structuredOutput: AiStructuredMode;
  maxOutputTokens: number;
  enabled: boolean;
  usedAs: ("DEFAULT" | "FALLBACK")[];
  updatedAt: string;
}

export interface AiModelList {
  items: AiModel[];
  /** Serverda `AI_SECRETS_KEY` bormi — yo'q bo'lsa kalit saqlab bo'lmaydi. */
  secretsConfigured: boolean;
}

export interface AiModelInput {
  name?: string;
  protocol?: AiProtocol;
  baseUrl?: string | null;
  modelId?: string;
  /** Yangi kalit; `null` — saqlangan kalitni o'chirish. */
  apiKey?: string | null;
  inputPriceMicroUsd?: number;
  outputPriceMicroUsd?: number;
  structuredOutput?: AiStructuredMode;
  maxOutputTokens?: number;
  enabled?: boolean;
}

export interface AiModelTestResult {
  ok: boolean;
  latencyMs: number;
  reply: string | null;
  structuredOk: boolean;
  inputTokens: number;
  outputTokens: number;
  error: string | null;
  errorKind: "TRANSIENT" | "PERMANENT" | null;
}

export interface AiFeatureSetting {
  feature: AiFeature;
  enabled: boolean;
  /** Bo'sh — asosiy model. */
  modelId: number | null;
  dailyUserLimit: number;
}

export interface AiSettings {
  enabled: boolean;
  monthlyBudgetCents: number;
  defaultModelId: number | null;
  fallbackModelId: number | null;
  features: AiFeatureSetting[];
}

export interface UpdateAiSettings {
  enabled?: boolean;
  monthlyBudgetCents?: number;
  defaultModelId?: number | null;
  fallbackModelId?: number | null;
}

export interface UpdateAiFeature {
  enabled?: boolean;
  modelId?: number | null;
  dailyUserLimit?: number;
}

export interface AiUsageTotals {
  calls: number;
  errors: number;
  fallbacks: number;
  inputTokens: number;
  outputTokens: number;
  costMicroUsd: number;
}

export interface AiUsageSummary {
  month: string;
  budgetMicroUsd: number;
  totals: AiUsageTotals;
  byFeature: (AiUsageTotals & { feature: string })[];
  byModel: (AiUsageTotals & { modelName: string })[];
  byDay: { day: string; calls: number; costMicroUsd: number }[];
}

/** AI boshqaruvi — backend'dagi `AiAdminController` (`/ai/admin`). */
export class AiApi extends BaseApi {
  private readonly baseUrl = "/ai/admin";

  models() {
    return this.request.get<AiModelList, ApiResponse<AiModelList>>(`${this.baseUrl}/models`);
  }

  createModel(dto: AiModelInput) {
    return this.request.post<AiModel, ApiResponse<AiModel>>(`${this.baseUrl}/models`, dto);
  }

  updateModel(id: number, dto: AiModelInput) {
    return this.request.patch<AiModel, ApiResponse<AiModel>>(`${this.baseUrl}/models/${id}`, dto);
  }

  removeModel(id: number) {
    return this.request.delete<null, ApiResponse<null>>(`${this.baseUrl}/models/${id}`);
  }

  /** Provayder xatosini tashlamaydi — sababi `error` da keladi. */
  testModel(id: number) {
    return this.request.post<AiModelTestResult, ApiResponse<AiModelTestResult>>(
      `${this.baseUrl}/models/${id}/test`,
    );
  }

  settings() {
    return this.request.get<AiSettings, ApiResponse<AiSettings>>(`${this.baseUrl}/settings`);
  }

  updateSettings(dto: UpdateAiSettings) {
    return this.request.patch<AiSettings, ApiResponse<AiSettings>>(`${this.baseUrl}/settings`, dto);
  }

  updateFeature(feature: AiFeature, dto: UpdateAiFeature) {
    return this.request.patch<AiFeatureSetting, ApiResponse<AiFeatureSetting>>(
      `${this.baseUrl}/features/${feature}`,
      dto,
    );
  }

  usage(month: string) {
    return this.request.get<AiUsageSummary, ApiResponse<AiUsageSummary>>(`${this.baseUrl}/usage`, {
      params: { month },
    });
  }
}

export const aiApi = new AiApi();

// ─── Moderatsiya: kurs tekshiruvi (#7) va kontent belgilari (#8) ──────────────

/** Mijoz uchun: qaysi AI funksiyalari yoqilgan (`GET /ai/features`, ochiq). */
export interface AiPublicFeatures {
  enabled: boolean;
  features: Partial<Record<AiFeature, boolean>>;
}

export type AiDraftStatus = "QUEUED" | "RUNNING" | "READY" | "FAILED" | "APPROVED" | "REJECTED";

/** Fonda tayyorlanadigan AI natijasi — backend'dagi `AiDraftDto`. */
export interface AiDraft<T = unknown> {
  id: number;
  feature: string;
  targetType: string;
  targetId: number;
  status: AiDraftStatus;
  /** Faqat READY/APPROVED/REJECTED holatida. */
  output: T | null;
  errorCode: string | null;
  attempts: number;
  createdAt: string;
  updatedAt: string;
  reviewedAt: string | null;
}

export type PrecheckKey =
  | "TITLE"
  | "DESCRIPTION"
  | "STRUCTURE"
  | "CATEGORY_FIT"
  | "LEVEL_FIT"
  | "LANGUAGE_QUALITY"
  | "PROHIBITED_CONTENT"
  | "CONTACT_INFO";
export type PrecheckStatus = "PASS" | "WARN" | "FAIL";

export interface PrecheckReport {
  /** 0–100. */
  score: number;
  summary: string;
  checks: { key: PrecheckKey; status: PrecheckStatus; note: string }[];
  suggestedCategory: string | null;
  issues: string[];
}

export type AiFlagSource = "CHAT" | "COMMENT" | "POST" | "POST_COMMENT";
export type AiFlagStatus = "OPEN" | "DISMISSED" | "RESOLVED";
export type AiFlagCategory =
  "ABUSE" | "HATE" | "SEXUAL" | "SPAM" | "CONTACT_SHARING" | "SELF_HARM" | "OTHER";

export interface AiContentFlag {
  id: number;
  source: AiFlagSource;
  sourceId: number;
  authorId: number | null;
  author: { id: number; firstName: string; lastName: string } | null;
  excerpt: string;
  category: AiFlagCategory;
  /** 1 (yengil) – 3 (og'ir). */
  severity: number;
  reason: string;
  status: AiFlagStatus;
  reviewedAt: string | null;
  createdAt: string;
}

export interface AiFlagQuery {
  status?: AiFlagStatus;
  page?: number;
  limit?: number;
}

/** Javob tili — panel tili. */
export type AiLanguage = "uz" | "ru" | "en";

/** AI moderatsiya vositalari — backend'dagi `AiController` va `AiModerationController`. */
export class AiModerationApi extends BaseApi {
  publicFeatures() {
    return this.request.get<AiPublicFeatures, ApiResponse<AiPublicFeatures>>("/ai/features");
  }

  draft<T>(id: number) {
    return this.request.get<AiDraft<T>, ApiResponse<AiDraft<T>>>(`/ai/drafts/${id}`);
  }

  latestPrecheck(courseId: number) {
    return this.request.get<
      AiDraft<PrecheckReport> | null,
      ApiResponse<AiDraft<PrecheckReport> | null>
    >(`/ai/moderation/courses/${courseId}/precheck`);
  }

  requestPrecheck(courseId: number, language: AiLanguage) {
    return this.request.post<AiDraft<PrecheckReport>, ApiResponse<AiDraft<PrecheckReport>>>(
      `/ai/moderation/courses/${courseId}/precheck`,
      { language },
    );
  }

  flags(query: AiFlagQuery) {
    return this.request.get<AiContentFlag[], Pagination<AiContentFlag>>("/ai/moderation/flags", {
      params: query,
    });
  }

  reviewFlag(id: number, status: "DISMISSED" | "RESOLVED") {
    return this.request.patch<AiContentFlag, ApiResponse<AiContentFlag>>(
      `/ai/moderation/flags/${id}`,
      { status },
    );
  }
}

export const aiModerationApi = new AiModerationApi();
